import moment from 'moment-timezone';

/**
 * Parsing, composition, and form-submit shape helpers.
 */

export const resetTimestampToUtc = (timestamp) => {
	const timeMoment = moment(timestamp);
	return moment.utc(timeMoment).format();
};

export const dateToHoneycombInput = (date) => {
	if (date === null) return null;
	const dateMoment = moment(date).tz(moment.tz.guess());
	return dateMoment.format('YYYY-MM-DD');
};

/**
 * Upper bound for date pickers — today + 5 years, in local YYYY-MM-DD.
 * Delegates to `dateToHoneycombInput` for the final format so the shape
 * matches what HC's <Input type="date"> expects.
 */
export const getLimitDate = (fromDate = moment()) => {
	const fromWrapped = moment(fromDate);
	return dateToHoneycombInput(fromWrapped.startOf('d').add(5, 'y'));
};

export const generateISOfromStrings = (dateString, timeString, timezone = moment.tz.guess()) => {
	const dateTimeString = `${dateString} ${timeString}`;
	const dateTimestringWithTimezone = moment.tz(dateTimeString, 'YYYY-MM-DD HH:mm:ss', timezone);
	return dateTimestringWithTimezone.toISOString();
};

export const updateDateWithTime = (
	date,
	time,
	withTimezone = false,
	timezone = moment.tz.guess()
) => {
	const base = withTimezone ? moment.tz(date, timezone) : moment(date);
	const [hours, minutes] = time.split(':').map(Number);
	base.set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
	return base.toISOString();
};

export const updateDateWithTimeAndTZ = (date, time, tz, inputFmt = 'YYYY-MM-DD HH:mm') => {
	const validTz = typeof tz === 'string' ? tz : tz?.value || moment.tz.guess();
	const m = moment.tz(`${date} ${time}`, inputFmt, true, validTz);
	if (!m.isValid()) throw new Error('Invalid datetime or format');
	return m.valueOf();
};

export const reverseFormTime = (fromTimestamp, toTimestamp, timezone = moment.tz.guess()) => {
	const validTz = typeof timezone === 'string' ? timezone : timezone?.value || moment.tz.guess();
	const fromMoment = moment.utc(fromTimestamp).tz(validTz);
	const toMoment = moment.utc(toTimestamp).tz(validTz);
	return {
		from: fromMoment.format('YYYY-MM-DD'),
		till: toMoment.format('YYYY-MM-DD'),
		fromTime: fromMoment.format('HH:mm'),
		tillTime: toMoment.format('HH:mm'),
	};
};

/**
 * GraphQL Date scalar: local date only, no time, no timezone → `YYYY-MM-DD`.
 * Returns `null` for anything that isn't a parseable ISO string.
 *
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
export function toRequestLocalDate(value) {
	if (!value || typeof value !== 'string') return null;

	const parsed = moment(value);

	if (!parsed.isValid()) return null;

	return parsed.format('YYYY-MM-DD');
}
